import * as React from "react";
import {
  Input,
  Dropdown,
  Option,
  Button,
  Checkbox,
  makeStyles,
  shorthands,
  useId,
  Field,
  Label,
  Switch,
  TabList,
  Tab,
  SelectTabEvent,
  SelectTabData,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Tooltip,
} from "@fluentui/react-components";
import { Info20Regular } from "@fluentui/react-icons";
import { Dialog, DialogSurface, DialogTitle, DialogContent, DialogActions } from "@fluentui/react-dialog";
import { useParams } from "react-router";
import FormHeader from "./FormHeader";
import ContractTableContact from "./ContractTableContact";
import DamagesTableContact from "./DamagesTableContact";
import RisksTableContact from "./RisksTableContact";
import { useLocation } from "react-router";
import ContactTable from "../pages/ContactTable";
const gebaeudeTypen = [
  "Einfamilienhaus",
  "Doppelhaus",
  "MFH",
  "Reihenhaus",
  "Bungalow",
];
const bauartklassen = [
  "BAK I",
  "BAK II",
  "BAK III",
  "BAK IV",
  "BAK V",
  "FHG I",
  "FHG II",
  "FHG III",
];
const dachgeschossStatus = [
  "nicht ausgebaut",
  "ausgebaut",
  "teilweise",
  "kein DG",
];

const dachTypen = ["Flachdach", "Pultdach", "Schleppdach", "Stufendach", "Spitzdach", "Walmdach", "Staffeldach", "Zeltdach"];
const sanierungTypen = ["Dach 2015", "Elektro 2020", "Heizung 2018"];
const gebaeudenutzungen = ["Wohnen", "Vermietung", "Gewerbe", "Sonstiges"];
const unterlagenEnum = [
  { value: 0, label: "nicht vorhanden" },
  { value: 1, label: "folgt" },
  { value: 2, label: "anbei" },
];
const schadensarten = ["Leitungswasser", "Brand", "Elementar"];

const useStyles = makeStyles({
  root: {
    ...shorthands.padding("20px"),
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 80px",
    alignItems: "center",
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "16px 80px",
    alignItems: "center",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "45% 55% ",
    alignItems: "center",
    gap: "8px",
    minHeight: "40px",
  },
  labelSection: {
    fontWeight: 600,
    fontSize: "18px",
    gridColumn: "1 / span 2",
    margin: "24px 0 8px 0",
  },
  label: {
    // entferne minWidth/maxWidth, damit es sich in der 45%-Spalte weiten kann:
    textAlign: "left",
    paddingRight: "8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontWeight: 400,
    fontSize: "16px",
    letterSpacing: "1px",
    position: "relative",
    "&::after": {
      content:
        "'........................................................................'",
      left: "100%",
      top: "0%",
      color: "#ccc",
      fontSize: "16px",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      width: "100%",
      overflow: "hidden",
    },
  },
  inputContainer: {
    width: "100%",
  },
  inputFullWidth: {
    width: "100%",
  },
  tableWrapper: {
    borderRadius: "12px",
    padding: "5px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  th: {
    fontWeight: 600,
    fontSize: "16px",
    padding: "12px 16px",
  },
  td: {
    fontSize: "15px",
    padding: "12px 16px",
    verticalAlign: "top",
  },
  striped: {
    background: "#fcfcfd",
  },
  tag: {
    display: "inline-block",
    background: "#8c7397ff",
    color: "#f3f6fa",
    borderRadius: "16px",
    padding: "4px 14px",
    fontSize: "14px",
    fontWeight: 500,
    marginRight: "8px",
    marginBottom: "4px",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.03)",
  },
  typ: {
    fontWeight: 600,
    color: "#3b3b3b",
    letterSpacing: "0.5px",
  },
  kommentar: {
    color: "#444",
    fontStyle: "italic",
  },
});

export interface RisikoFormValues {
  gebaeudeId?: string;
  gebaeudetyp?: string;
  bauartklasse?: string;
  baujahr?: number;
  adresse?: {
    plz?: string;
    ort?: string;
    strasse?: string;
    hausnummer?: string;
  };
  selbstnutzung?: boolean;
  gebaeudenutzung?: string;
  sanierungsdetails?: Array<{
    sanierungsid?: string;
    sanierungsart?: string;
    jahr?: number;
  }>;
  sanierung?: string;
  dachgeschossstatus?: string;
  dach?: string;
  kellerflaeche_m2?: number;
  anzahlGeschosse?: number;
  anzahlWohneinheiten?: number;
  wohnflaeche_eg_m2?: number;
  wohnflaeche_og_m2?: number;
  gewerbeflaeche_m2?: number;
  hobbyWohnflaecheNebengebaeude_m2?: number;
  bauausfuehrung?: string;
  mieteinnahmen?: number;
  wiederaufbauwertNachbarbebauung?: number;
  unterlagen?: {
    gutachten?: number;
    flaechenberechnung?: number;
    nebenkostenabrechnung?: number;
  };
  gebaeudeschaeden?: Array<{
    schadensart?: string;
    jahr?: number;
    schadenaufwand?: number;
  }>;
}
interface RisikoFormProps {
  initialValues?: RisikoFormValues;
}

const gehobeneBauausfuehrung = [
  { beschreibung: "Dach", tags: ["Naturschieferdach", "Kupferdach"] },
  { beschreibung: "Außenwände", tags: ["Natursteinverkleidung", "Keramikverkleidung", "Kunststeinverkleidung", "Klinker"] },
  { beschreibung: "Decken/Wände", tags: ["Stuckarbeiten", "Edelholzverkleidungen"] },
  { beschreibung: "Fußböden", tags: ["Naturstein", "Parkett", "Teppich (hochwertig)"] },
  { beschreibung: "Fenster", tags: ["Holzsprossenfenster"] },
  { beschreibung: "Türen", tags: ["Edelholz"] },
  { beschreibung: "Sanitär", tags: ["hochwertige sanitäre Einrichtung"] },
  { beschreibung: "Heizung", tags: ["Wärmepumpen", "Fußbodenheizung", "Deckenheizung", "Solaranlagen"] },
  { beschreibung: "Strom", tags: ["Photovoltaikanlagen"] },
  { beschreibung: "Wellness", tags: ["Schwimmbäder", "Whirlpool", "Sauna"] },
];

const mindereBauausfuehrung = [
  { beschreibung: "Fußböden", tags: ["PVC-Boden auf Estrich"] },
  { beschreibung: "Fenster", tags: ["Einfaches Fensterglas"] },
  { beschreibung: "Sanitär", tags: ["Ohne Bad / Dusche"] },
  { beschreibung: "Heizung", tags: ["Ofenheizung"] },
];

const bauausfuehrung = [
  ...gehobeneBauausfuehrung.map((row) => ({
    ...row,
    typ: "gehoben",
  })),
  ...mindereBauausfuehrung.map((row) => ({
    ...row,
    typ: "minder",
  })),
];

const beispielBauausfuehrung = [
  {
    typ: "gehoben",
    objekt: "Dach",
    ausstattung: ["Kupferdach"],
    kommentar: "Sanierung 2020, sehr langlebig",
  },
  {
    typ: "minder",
    objekt: "Fußböden",
    ausstattung: ["PVC-Boden auf Estrich"],
    kommentar: "Nur im OG, EG mit Fliesen",
  },
  {
    typ: "gehoben",
    objekt: "Fußböden",
    ausstattung: ["Parkett", "Naturstein"],
    kommentar: "EG Naturstein, DG Parkett",
  },
  {
    typ: "gehoben",
    objekt: "Sanitär",
    ausstattung: ["hochwertige sanitäre Einrichtung"],
    kommentar: "Badezimmer mit Whirlpool",
  },
  {
    typ: "minder",
    objekt: "Fenster",
    ausstattung: ["Einfaches Fensterglas"],
    kommentar: "Austausch geplant 2025",
  },
  {
    typ: "gehoben",
    objekt: "Heizung",
    ausstattung: ["Wärmepumpe", "Fußbodenheizung"],
    kommentar: "Energieeffizient, installiert 2018",
  },
  {
    typ: "gehoben",
    objekt: "Wellness",
    ausstattung: ["Schwimmbad", "Sauna"],
    kommentar: "Kellerbereich",
  },
  {
    typ: "minder",
    objekt: "Sanitär",
    ausstattung: ["Ohne Bad / Dusche"],
    kommentar: "Nur Gäste-WC vorhanden",
  },
];

function renderTagButtons(tags: string[], styles: any) {
  return (
    <div>
      {tags.map((tag) => (
        <span key={tag} className={styles.tag}>{tag}</span>
      ))}
    </div>
  );
}

export const RisikoImmoForm: React.FC<RisikoFormProps> = ({
  initialValues = {} as RisikoFormValues,
}) => {
  const styles = useStyles();

  const { gebaeudeId } = useParams<{ gebaeudeId: string }>();  

  const [values, setValues] = React.useState<RisikoFormValues>(initialValues);

  // Hilfsfunktionen für einfache Änderungen
  const handleChange = (field: keyof RisikoFormValues, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };
  
  const handleAdresseChange = (field: keyof NonNullable<RisikoFormValues["adresse"]>, value: any) => {
    setValues((prev) => ({
      ...prev,
      adresse: { ...prev.adresse, [field]: value },
    }));
  };

  const handleUnterlagenChange = (field: keyof NonNullable<RisikoFormValues["unterlagen"]>, value: any) => {
    setValues((prev) => ({
      ...prev,
      unterlagen: { ...prev.unterlagen, [field]: value },
    }));
  };

  const handleSchadenChange = (
    idx: number,
    field: keyof NonNullable<RisikoFormValues["gebaeudeschaeden"]>[number],
    value: any
  ) => {
    setValues((prev) => ({
      ...prev,
      gebaeudeschaeden: prev.gebaeudeschaeden?.map((s, i) =>
        i === idx ? { ...s, [field]: value } : s
      ),
    }));
  };
const getUnterlagenLabel = (val?: number) => {
  const found = unterlagenEnum.find(u => u.value === val);
  return found ? found.label : "";
};
  const [isDachPopupOpen, setIsDachPopupOpen] = React.useState(false);
  const [isSanierungPopupOpen, setIsSanierungPopupOpen] = React.useState(false);
  const [selectedDach, setSelectedDach] = React.useState(values.dach ?? "");
  const [selectedSanierung, setSelectedSanierung] = React.useState(values.sanierung ?? "");
  const [selectedTab, setSelectedTab] = React.useState("risiko");
  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as string);
  };

  return (

    <div className={styles.root}>
      <FormHeader
        title={
          initialValues.gebaeudeId != ""
            ? initialValues.adresse?.strasse + " " + initialValues.adresse?.hausnummer + ", " + initialValues.adresse?.plz + " " + 
              initialValues.adresse?.ort + " - " + initialValues.gebaeudetyp
            : "Neues Risikoobjekt"
        }
      />
      <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
        <Tab style={{ paddingLeft: 0 }} id="risiko" value="risiko">
          Allgemein
        </Tab>
        <Tab id="contacts" value="contacts">
          Kontakte/Kontaktverbindung
          <sup style={{ marginLeft: "5px", color: "green" }}>5</sup>
        </Tab>
        <Tab id="vertraege" value="vertraege">
          Verträge <sup style={{ marginLeft: "5px", color: "green" }}>4</sup>
        </Tab>
        <Tab id="schaeden" value="schaeden">
          Schäden<sup style={{ marginLeft: "5px", color: "red" }}>2</sup>
        </Tab>
        <Tab id="events" value="events">
          Vorgänge <sup style={{ marginLeft: "5px", color: "red" }}>4</sup>
          <sup style={{ marginLeft: "5px", color: "green" }}>1</sup>
        </Tab>
        <Tab id="notes" value="notes">
          Notizen
        </Tab>
      </TabList>

      {selectedTab === "risiko" && (
        <>
          <div className={styles.grid}>
            {/* Risikoort */}
            <span className={styles.labelSection}>Risikoort</span>

            {/* Adresse */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>PLZ</span>
              <Input
                value={values.adresse?.plz ?? ""}
                onChange={(_, d) => handleAdresseChange("plz", d.value)}
                className={styles.inputFullWidth}
              />
            </div>
            <div className={styles.fieldRow}>
              <span className={styles.label}>Ort</span>
              <Input
                value={values.adresse?.ort ?? ""}
                onChange={(_, d) => handleAdresseChange("ort", d.value)}
                className={styles.inputFullWidth}
              />
            </div>
            <div className={styles.fieldRow}>
              <span className={styles.label}>Straße</span>
              <Input
                value={values.adresse?.strasse ?? ""}
                onChange={(_, d) => handleAdresseChange("strasse", d.value)}
                className={styles.inputFullWidth}
              />
            </div>
            <div className={styles.fieldRow}>
              <span className={styles.label}>Hausnummer</span>
              <Input
                value={values.adresse?.hausnummer ?? ""}
                onChange={(_, d) => handleAdresseChange("hausnummer", d.value)}
                className={styles.inputFullWidth}
              />
            </div>

        {/* Gebäudetyp/-nutzung */}
        <span className={styles.labelSection}>Gebäudetyp/-nutzung</span>

        {/* Gebäudetyp */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>
            Gebäudetyp
            <Tooltip content="Bitte wählen Sie Ihren aktuellen Gebäudetyp aus." relationship="label">
              <span style={{ marginLeft: 8, verticalAlign: "middle", cursor: "pointer" }}>
                <Info20Regular />
              </span>
            </Tooltip>
          </span>
          <Dropdown
            value={values.gebaeudetyp ?? ""}
            onOptionSelect={(_, d) => handleChange("gebaeudetyp", d.optionValue)}
            className={styles.inputFullWidth}
          >
            {gebaeudeTypen.map((typ) => (
              <Option key={typ} value={typ}>{typ}</Option>
            ))}
          </Dropdown>
        </div>
        {/* Bauartklasse */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Bauartklasse</span>
          <Dropdown
            value={values.bauartklasse ?? ""}
            onOptionSelect={(_, d) => handleChange("bauartklasse", d.optionValue)}
            className={styles.inputFullWidth}
          >
            {bauartklassen.map((b) => (
              <Option key={b} value={b}>{b}</Option>
            ))}
          </Dropdown>
        </div>
        {/* Selbstnutzung */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Selbstnutzung</span>
          <Switch
            checked={!!values.selbstnutzung}
            onChange={(_, d) => handleChange("selbstnutzung", d.checked)}
          />
        </div>
        {/* Baujahr */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Baujahr</span>
          <Input
            type="number"
            value={values.baujahr?.toString() ?? ""}
            onChange={(_, d) => handleChange("baujahr", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
        {/* Gebäudenutzung */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Gebäudenutzung</span>
          <Dropdown
            value={values.gebaeudenutzung ?? ""}
            onOptionSelect={(_, d) => handleChange("gebaeudenutzung", d.optionValue)}
            className={styles.inputFullWidth}
          >
            {gebaeudenutzungen.map((n) => (
              <Option key={n} value={n}>{n}</Option>
            ))}
          </Dropdown>
        </div>

        {/* Bauweise */}
        <span className={styles.labelSection}>Bauweise</span>

        {/* Dachgeschossstatus */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Dachgeschossstatus</span>
          <Dropdown
            value={values.dachgeschossstatus ?? ""}
            onOptionSelect={(_, d) => handleChange("dachgeschossstatus", d.optionValue)}
            className={styles.inputFullWidth}
          >
            {dachgeschossStatus.map((d) => (
              <Option key={d} value={d}>{d}</Option>
            ))}
          </Dropdown>
        </div>
        {/* Dach */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Dach</span>
          <button
            type="button"
            className={styles.inputFullWidth}
            style={{
              height: 36,
              border: "1px solid #ccc",
              borderRadius: 4,
              background: "#fff",
              textAlign: "left",
              paddingLeft: 12,
              cursor: "pointer",
            }}
            onClick={() => setIsDachPopupOpen(true)}
          >
            {values.dach || "Dachtyp wählen"}
          </button>
        </div>

        {/* Kellerfläche */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Kellerfläche (m²)</span>
          <Input
            type="number"
            value={values.kellerflaeche_m2?.toString() ?? ""}
            onChange={(_, d) => handleChange("kellerflaeche_m2", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
        {/* Anzahl Geschosse */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Anzahl Geschosse</span>
          <Input
            type="number"
            value={values.anzahlGeschosse?.toString() ?? ""}
            onChange={(_, d) => handleChange("anzahlGeschosse", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
        {/* Anzahl Wohneinheiten */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Anzahl Wohneinheiten</span>
          <Input
            type="number"
            value={values.anzahlWohneinheiten?.toString() ?? ""}
            onChange={(_, d) => handleChange("anzahlWohneinheiten", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
        {/* Wohnfläche EG */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Wohnfläche EG (m²)</span>
          <Input
            type="number"
            value={values.wohnflaeche_eg_m2?.toString() ?? ""}
            onChange={(_, d) => handleChange("wohnflaeche_eg_m2", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
        {/* Wohnfläche OG */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Wohnfläche OG (m²)</span>
          <Input
            type="number"
            value={values.wohnflaeche_og_m2?.toString() ?? ""}
            onChange={(_, d) => handleChange("wohnflaeche_og_m2", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
        {/* Gewerbefläche */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Gewerbefläche (m²)</span>
          <Input
            type="number"
            value={values.gewerbeflaeche_m2?.toString() ?? ""}
            onChange={(_, d) => handleChange("gewerbeflaeche_m2", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
        {/* Hobby-/Wohnfläche Nebengebäude */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Hobby-/Wohnfläche Nebengebäude (m²)</span>
          <Input
            type="number"
            value={values.hobbyWohnflaecheNebengebaeude_m2?.toString() ?? ""}
            onChange={(_, d) => handleChange("hobbyWohnflaecheNebengebaeude_m2", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
        
        {/* Sonstiges */}
        <span className={styles.labelSection}>Sonstiges</span>

        {/* Mieteinnahmen */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Mieteinnahmen</span>
          <Input
            type="number"
            value={values.mieteinnahmen?.toString() ?? ""}
            onChange={(_, d) => handleChange("mieteinnahmen", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>

        {/* Unterlagen */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Gutachten</span>
          <Dropdown
            value={getUnterlagenLabel(values.unterlagen?.gutachten)}
            onOptionSelect={(_, d) => handleUnterlagenChange("gutachten", Number(d.optionValue))}
            className={styles.inputFullWidth}
          >
            {unterlagenEnum.map((u) => (
              <Option key={u.value} value={u.value.toString()}>{u.label}</Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Flächenberechnung</span>
          <Dropdown
            value={getUnterlagenLabel(values.unterlagen?.flaechenberechnung)}
            onOptionSelect={(_, d) => handleUnterlagenChange("flaechenberechnung", Number(d.optionValue))}
            className={styles.inputFullWidth}
          >
            {unterlagenEnum.map((u) => (
              <Option key={u.value} value={u.value.toString()}>{u.label}</Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Nebenkostenabrechnung</span>
          <Dropdown
            value={getUnterlagenLabel(values.unterlagen?.nebenkostenabrechnung)}
            onOptionSelect={(_, d) => handleUnterlagenChange("nebenkostenabrechnung", Number(d.optionValue))}
            className={styles.inputFullWidth}
          >
            {unterlagenEnum.map((u) => (
              <Option key={u.value} value={u.value.toString()}>{u.label}</Option>
            ))}
          </Dropdown>
        </div>

        {/* Wiederaufbauwert Nachbarbebauung */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Wiederaufbauwert Nachbarbebauung</span>
          <Input
            type="number"
            value={values.wiederaufbauwertNachbarbebauung?.toString() ?? ""}
            onChange={(_, d) => handleChange("wiederaufbauwertNachbarbebauung", Number(d.value))}
            className={styles.inputFullWidth}
          />
        </div>
      </div>

        {/* Bauausführung */}

<Label size="large" style={{ fontWeight: 700, fontSize: 22, marginBottom: 16, display: "block" }}>
  Bauausführung
</Label>
<div className={styles.tableWrapper}>
  <Table aria-label="Bauausführung" style={{ width: "100%" }}>
    <TableHeader>
      <TableRow>
        <TableHeaderCell className={styles.th}>Typ</TableHeaderCell>
        <TableHeaderCell className={styles.th}>Objekt</TableHeaderCell>
        <TableHeaderCell className={styles.th}>Ausstattung</TableHeaderCell>
        <TableHeaderCell className={styles.th}>Kommentar</TableHeaderCell>
      </TableRow>
    </TableHeader>
    <TableBody>
      {beispielBauausfuehrung.map((row, idx) => (
        <TableRow key={idx} className={idx % 2 === 1 ? styles.striped : undefined}>
          <TableCell className={styles.td}>
            <span className={styles.typ}>
              {row.typ === "gehoben" ? "Gehobene Bauausführung" : "Mindere Bauausführung"}
            </span>
          </TableCell>
          <TableCell className={styles.td}>{row.objekt}</TableCell>
          <TableCell className={styles.td}>{renderTagButtons(row.ausstattung, styles)}</TableCell>
          <TableCell className={styles.td}>
            <span className={styles.kommentar}>{row.kommentar}</span>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</div>
      <div style={{ display: "flex", gap: 32, marginTop: 16 }}>
        {/* Gebäude-Schäden */}
        <div style={{ flex: 1 }}>
          <Label size="large">Gebäude-Schäden</Label>
          <Table aria-label="Gebäude-Schäden">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Schadensart</TableHeaderCell>
                <TableHeaderCell>Jahr</TableHeaderCell>
                <TableHeaderCell>Schadenaufwand</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {values.gebaeudeschaeden && values.gebaeudeschaeden.length > 0 ? (
                values.gebaeudeschaeden.map((schaden, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{schaden.schadensart ?? "-"}</TableCell>
                    <TableCell>{schaden.jahr ?? "-"}</TableCell>
                    <TableCell>
                      {schaden.schadenaufwand != null
                        ? schaden.schadenaufwand.toLocaleString("de-DE") + " €"
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} style={{ color: "#888" }}>
                    Keine Schäden vorhanden.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Sanierungsdetails */}
        <div style={{ flex: 1 }}>
          <Label size="large">Sanierungsdetails</Label>
          <Table aria-label="Sanierungsdetails" style={{ marginTop: 8 }}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Jahr</TableHeaderCell>
                <TableHeaderCell>Beschreibung</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {values.sanierungsdetails && values.sanierungsdetails.length > 0 ? (
                values.sanierungsdetails.map((detail, idx) => (
                  <TableRow key={detail.sanierungsid ?? idx}>
                    <TableCell>{detail.jahr ?? "-"}</TableCell>
                    <TableCell>{detail.sanierungsart ?? "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} style={{ color: "#888" }}>
                    Keine Sanierungsdetails vorhanden.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={isDachPopupOpen} onOpenChange={(_, d) => setIsDachPopupOpen(d.open)}>
        <DialogSurface style={{ minWidth: "1300px"}}>
          <DialogTitle>Dachtyp wählen</DialogTitle>
          <DialogContent>
            <div style={{ display: "flex", gap: 32, marginTop: 10, marginBottom: 10, justifyContent: "center", width: "1200px", flexWrap: "wrap" }}>
              {dachTypen.map((dach) => (
                <label key={dach} style={{ textAlign: "center", cursor: "pointer" }}>
                  <img
                    src={require(`../assets/dach/${dach.toLowerCase()}.jpg`)}
                    alt={dach}
                    style={{
                      width: 220,
                      height: 180,
                      border: selectedDach === dach ? "3px solid #361836" : "1px solid #ccc",
                      borderRadius: 8,
                      marginBottom: 8,
                      objectFit: "cover",
                    }}
                    onClick={() => setSelectedDach(dach)}
                  />
                  <div>
                    <input
                      type="radio"
                      name="dachtyp"
                      checked={selectedDach === dach}
                      onChange={() => setSelectedDach(dach)}
                      style={{ marginRight: 6 }}
                    />
                    {dach}
                  </div>
                </label>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <button
              onClick={() => setIsDachPopupOpen(false)}
            >
              Abbrechen
            </button>
            <button
              onClick={() => {
                handleChange("dach", selectedDach);
                setIsDachPopupOpen(false);
              }}
              disabled={!selectedDach}
            >
              Übernehmen
            </button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      <Dialog open={isSanierungPopupOpen} onOpenChange={(_, d) => setIsSanierungPopupOpen(d.open)}>
        <DialogSurface>
          <DialogTitle>Sanierung wählen</DialogTitle>
          <DialogContent>
            <div style={{ display: "flex", gap: 32, marginTop: 10, marginBottom: 10, justifyContent: "center" }}>
              {sanierungTypen.map((sanierung) => (
                <label key={sanierung} style={{ textAlign: "center", cursor: "pointer" }}>
                  <img
                    src={require(`../assets/dach/spitzdach.jpg`)}
                    alt={sanierung}
                    style={{
                      width: 150,
                      height: 180,
                      border: selectedSanierung === sanierung ? "3px solid #361836" : "1px solid #ccc",
                      borderRadius: 8,
                      marginBottom: 8,
                      objectFit: "cover",
                    }}
                    onClick={() => setSelectedSanierung(sanierung)}
                  />
                  <div>
                    <input
                      type="radio"
                      name="dachtyp"
                      checked={selectedDach === sanierung}
                      onChange={() => setSelectedDach(sanierung)}
                      style={{ marginRight: 6 }}
                    />
                    {sanierung}
                  </div>
                </label>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <button
              onClick={() => setIsDachPopupOpen(false)}
            >
              Abbrechen
            </button>
            <button
              onClick={() => {
                handleChange("dach", selectedDach);
                setIsDachPopupOpen(false);
              }}
              disabled={!selectedDach}
            >
              Übernehmen
            </button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
          <Dialog
            open={isSanierungPopupOpen}
            onOpenChange={(_, d) => setIsSanierungPopupOpen(d.open)}
          >
            <DialogSurface>
              <DialogTitle>Sanierung wählen</DialogTitle>
              <DialogContent>
                <div
                  style={{
                    display: "flex",
                    gap: 32,
                    marginTop: 10,
                    marginBottom: 10,
                    justifyContent: "center",
                  }}
                >
                  {sanierungTypen.map((sanierung) => (
                    <label
                      key={sanierung}
                      style={{ textAlign: "center", cursor: "pointer" }}
                    >
                      <img
                        src={require(`../assets/dach/spitzdach.jpg`)}
                        alt={sanierung}
                        style={{
                          width: 150,
                          height: 180,
                          border:
                            selectedSanierung === sanierung
                              ? "3px solid #361836"
                              : "1px solid #ccc",
                          borderRadius: 8,
                          marginBottom: 8,
                          objectFit: "cover",
                        }}
                        onClick={() => setSelectedSanierung(sanierung)}
                      />
                      <div>
                        <input
                          type="radio"
                          name="dachtyp"
                          checked={selectedDach === sanierung}
                          onChange={() => setSelectedDach(sanierung)}
                          style={{ marginRight: 6 }}
                        />
                        {sanierung}
                      </div>
                    </label>
                  ))}
                </div>
              </DialogContent>
              <DialogActions>
                <button onClick={() => setIsDachPopupOpen(false)}>
                  Abbrechen
                </button>
                <button
                  onClick={() => {
                    handleChange("dach", selectedDach);
                    setIsDachPopupOpen(false);
                  }}
                  disabled={!selectedDach}
                >
                  Übernehmen
                </button>
              </DialogActions>
            </DialogSurface>
          </Dialog>
        </>
      )}
      {selectedTab === "schaeden" && (
        <div style={{ display: "flex", gap: 32, marginTop: 16 }}>
          {/* Gebäude-Schäden */}
          <div style={{ flex: 1 }}>
            <Label size="large">Gebäude-Schäden</Label>
            <Table aria-label="Gebäude-Schäden">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Schadensart</TableHeaderCell>
                  <TableHeaderCell>Jahr</TableHeaderCell>
                  <TableHeaderCell>Schadenaufwand</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {values.gebaeudeschaeden &&
                values.gebaeudeschaeden.length > 0 ? (
                  values.gebaeudeschaeden.map((schaden, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{schaden.schadensart ?? "-"}</TableCell>
                      <TableCell>{schaden.jahr ?? "-"}</TableCell>
                      <TableCell>
                        {schaden.schadenaufwand != null
                          ? schaden.schadenaufwand.toLocaleString("de-DE") +
                            " €"
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} style={{ color: "#888" }}>
                      Keine Schäden vorhanden.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Sanierungsdetails */}
          <div style={{ flex: 1 }}>
            <Label size="large">Sanierungsdetails</Label>
            <Table aria-label="Sanierungsdetails" style={{ marginTop: 8 }}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Jahr</TableHeaderCell>
                  <TableHeaderCell>Beschreibung</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {values.sanierungsdetails &&
                values.sanierungsdetails.length > 0 ? (
                  values.sanierungsdetails.map((detail, idx) => (
                    <TableRow key={detail.sanierungsid ?? idx}>
                      <TableCell>{detail.jahr ?? "-"}</TableCell>
                      <TableCell>{detail.sanierungsart ?? "-"}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} style={{ color: "#888" }}>
                      Keine Sanierungsdetails vorhanden.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      {selectedTab === "vertraege" && <ContractTableContact kontaktId={3} />}
      {selectedTab === "contacts" && <ContactTable />}
    </div>
  );
};